/* =====================================================================
 * DocumentSidebar — 사이드바 내 문서 목록 섹션
 * ===================================================================== */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  FileText,
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useDocumentStore } from '@auw/stores';

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const DocumentSidebar: React.FC = () => {
  const documents = useDocumentStore((s) => s.documents);
  const documentOrder = useDocumentStore((s) => s.documentOrder);
  const activeDocumentId = useDocumentStore((s) => s.activeDocumentId);
  const createDocument = useDocumentStore((s) => s.createDocument);
  const deleteDocument = useDocumentStore((s) => s.deleteDocument);
  const renameDocument = useDocumentStore((s) => s.renameDocument);
  const setActiveDocument = useDocumentStore((s) => s.setActiveDocument);

  const [isOpen, setIsOpen] = useState(true);
  const [menuDocId, setMenuDocId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');

  const menuRef = useRef<HTMLDivElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  /* ── 메뉴 외부 클릭 닫기 ── */
  useEffect(() => {
    if (!menuDocId) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuDocId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuDocId]);

  /* ── 이름 편집 포커스 ── */
  useEffect(() => {
    if (editingId) editInputRef.current?.focus();
  }, [editingId]);

  /* ── 핸들러 ── */
  const handleCreate = useCallback(() => {
    createDocument();
  }, [createDocument]);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 4, left: rect.left });
      setMenuDocId(id);
    },
    [],
  );

  const handleRename = useCallback(
    (id: string) => {
      setEditingId(id);
      setEditTitle(documents[id]?.title || '');
      setMenuDocId(null);
    },
    [documents],
  );

  const handleRenameSubmit = useCallback(
    (id: string) => {
      if (editTitle.trim()) {
        renameDocument(id, editTitle.trim());
      }
      setEditingId(null);
    },
    [editTitle, renameDocument],
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteDocument(id);
      setMenuDocId(null);
    },
    [deleteDocument],
  );

  /* ── 날짜 포맷 ── */
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);

    if (mins < 1) return '방금 전';
    if (mins < 60) return `${mins}분 전`;
    if (hours < 24) return `${hours}시간 전`;
    if (days < 7) return `${days}일 전`;
    return `${d.getMonth() + 1}/${d.getDate()}`;
  };

  return (
    <>
      <SectionHeader onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <SectionTitle>문서</SectionTitle>
        <DocCount>{documentOrder.length}</DocCount>
        <AddButton
          onClick={(e) => {
            e.stopPropagation();
            handleCreate();
          }}
          title="새 문서"
        >
          <Plus size={14} />
        </AddButton>
      </SectionHeader>

      {isOpen && (
        <DocList>
          {documentOrder.length === 0 ? (
            <EmptyHint>문서가 없습니다</EmptyHint>
          ) : (
            documentOrder.map((id) => {
              const doc = documents[id];
              if (!doc) return null;

              return (
                <DocItem
                  key={id}
                  $active={id === activeDocumentId}
                  onClick={() => setActiveDocument(id)}
                >
                  <DocIcon>
                    <FileText size={14} />
                  </DocIcon>
                  <DocInfo>
                    {editingId === id ? (
                      <RenameInput
                        ref={editInputRef}
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRenameSubmit(id);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        onBlur={() => handleRenameSubmit(id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <DocTitle>{doc.title || '제목 없는 문서'}</DocTitle>
                    )}
                    <DocDate>{formatDate(doc.updatedAt)}</DocDate>
                  </DocInfo>
                  <MoreButton
                    onClick={(e) => handleContextMenu(e, id)}
                    className="more-btn"
                  >
                    <MoreHorizontal size={14} />
                  </MoreButton>
                </DocItem>
              );
            })
          )}
        </DocList>
      )}

      {/* ── 컨텍스트 메뉴 ── */}
      {menuDocId && (
        <ContextMenu
          ref={menuRef}
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          <ContextItem onClick={() => handleRename(menuDocId)}>
            <Pencil size={14} />
            이름 변경
          </ContextItem>
          <ContextItem $danger onClick={() => handleDelete(menuDocId)}>
            <Trash2 size={14} />
            삭제
          </ContextItem>
        </ContextMenu>
      )}
    </>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  cursor: pointer;
  user-select: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const SectionTitle = styled.span`
  flex: 1;
`;

const DocCount = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 400;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const DocList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 0 6px;
`;

const DocItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  background: ${({ $active, theme }) =>
    $active
      ? theme.mode === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.06)'
      : 'transparent'};
  transition: background 100ms;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  }

  .more-btn {
    opacity: 0;
    transition: opacity 100ms;
  }

  &:hover .more-btn {
    opacity: 1;
  }
`;

const DocIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocTitle = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DocDate = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const RenameInput = styled.input`
  display: block;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.8125rem;
  font-weight: 500;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  outline: none;

  &:focus {
    border-color: #8b5cf6;
  }
`;

const MoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  }
`;

const EmptyHint = styled.div`
  padding: 12px 16px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
`;

const ContextMenu = styled.div`
  position: fixed;
  z-index: 200;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 4px;
  min-width: 140px;
`;

const ContextItem = styled.button<{ $danger?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: none;
  border-radius: 4px;
  background: transparent;
  font-size: 0.8125rem;
  color: ${({ $danger, theme }) =>
    $danger ? '#ef4444' : theme.colors.textPrimary};
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${({ $danger, theme }) =>
      $danger
        ? 'rgba(239,68,68,0.1)'
        : theme.mode === 'dark'
          ? 'rgba(255,255,255,0.06)'
          : 'rgba(0,0,0,0.04)'};
  }
`;
